import React from 'react';
import { buildStaticMap } from './staticMap';
import { toPdfSections } from './reportPdfSections';
import { prefetchImages } from './pdfImages.web';
import type { ReportModel } from './reportModel';
import { triggerDownload } from './downloadFile';
import { ensureImportMetaRegistry } from './pdfRuntime.web';

export const isPdfDownloadSupported = true;

export interface DownloadReportPdfResult {
  omittedFigureCount: number;
  imagesFailedCount: number;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
}

function filenameFor(report: ReportModel): string {
  const address = report.structure.address || report.structure.id;
  const date = new Date().toISOString().slice(0, 10);
  return `REDI-readiness-record-${slugify(address)}-${date}.pdf`;
}

/**
 * Build the property readiness record as a PDF and hand it to the browser
 * as a download. Everything heavy (`@react-pdf/renderer` and its
 * dependents) is imported lazily so this module can be imported statically
 * from the report screen without growing the entry bundle.
 */
export async function downloadReportPdf(report: ReportModel): Promise<DownloadReportPdfResult> {
  ensureImportMetaRegistry();

  const pdfSections = toPdfSections(report);
  const { latitude, longitude } = report.structure.centroid;
  const mapSpec = buildStaticMap(latitude, longitude, { zoom: 17 });
  const mapTileUrls = mapSpec.tiles.map((tile) => tile.url);

  const [{ pdf }, { ReportDocument }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('../components/report/ReportDocument.web'),
  ]);

  const { map: images, failedCount } = await prefetchImages([
    ...pdfSections.imageUrls,
    ...mapTileUrls,
  ]);

  const doc = React.createElement(ReportDocument, {
    sections: pdfSections,
    images,
    latitude,
    longitude,
  });

  // ReportDocument's own props aren't @react-pdf's <Document> props (they
  // are the report data plus the prefetched image map), so `pdf()`'s prop
  // type doesn't line up even though the rendered tree is a valid
  // <Document>. Cast at this one boundary rather than loosening either
  // component's real prop types.
  const blob = await pdf(doc as Parameters<typeof pdf>[0]).toBlob();
  triggerDownload(blob, filenameFor(report));

  return { omittedFigureCount: pdfSections.omittedFigureCount, imagesFailedCount: failedCount };
}
